/**
 * Hook for managing Pixlee widget instances with lazy loading support
 * Registry pattern prevents duplicate widget initialization
 * Tracks widgets by ID to prevent duplicates
 *
 * @see https://developers.pixlee.com/docs/pixlee-widget-user-guide#lazy-loading
 */
import {useEffect, useRef, useState} from 'react';
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer';
import {usePixleeScript} from './use-pixlee-script';

/**
 * Global widget registry - Tracks initialized widgets by containerId
 * Prevents duplicate widget initialization
 * Key: containerId, Value: { widgetId, initialized }
 */
const widgetRegistry = new Map();

/**
 * Initialize a Pixlee widget by ID with custom container
 * Prevents duplicates - safe to call multiple times
 *
 * @param {string|number} widgetId - The Pixlee widget ID
 * @param {string} containerId - The container element ID for this widget
 * @returns {boolean} success - True if widget initialized successfully
 */
function initializeWidget(widgetId, containerId) {
  if (!widgetId || !window.Pixlee || !containerId) {
    return false;
  }

  const registryKey = containerId;

  // Already initialized for this container - skip
  if (widgetRegistry.has(registryKey)) {
    const existing = widgetRegistry.get(registryKey);

    if (existing.initialized && existing.widgetId === String(widgetId)) {
      return true;
    }
  }

  // Check if container element exists
  const containerElement = document.getElementById(containerId);

  if (!containerElement) {
    console.warn(`Pixlee: Container element #${containerId} not found`);

    return false;
  }

  try {
    window.Pixlee.addSimpleWidget({
      widgetId: Number(widgetId),
      containerId: containerId,
      subscribedEvents: ['widgetLoaded', 'photoOpened', 'ctaClicked'],
      fixedWidth: false,
    });

    // Track initialized widget
    widgetRegistry.set(registryKey, {
      widgetId: String(widgetId),
      initialized: true,
    });

    // Resize widget after initialization (as recommended by Pixlee docs)
    setTimeout(() => {
      if (window.Pixlee?.resizeWidget) {
        window.Pixlee.resizeWidget();
      }
    }, 100);

    return true;
  } catch (error) {
    console.error(`Pixlee widget initialization error for widget ${widgetId} in container ${containerId}:`, error);

    return false;
  }
}

/**
 * Cleanup widget by container ID
 * Removes from registry to allow re-initialization
 *
 * @param {string} containerId - The container element ID
 */
function cleanupWidget(containerId) {
  if (!containerId) {
    return;
  }

  widgetRegistry.delete(containerId);

  // Only close Pixlee if no widgets are active
  // Pass true to indicate this is a single page app
  if (widgetRegistry.size === 0 && window.Pixlee?.close) {
    try {
      window.Pixlee.close(true);
    } catch (error) {
      console.error('Pixlee cleanup error:', error);
    }
  }
}

/**
 * Initialize and manage a Pixlee widget with lazy loading support
 * Widget only initializes when container becomes visible in viewport
 *
 * @param {string|number} widgetId - The Pixlee widget ID from Contentful
 * @param {Object} options - Configuration options
 * @param {string} options.containerId - Custom container ID (required for multiple widgets)
 * @param {boolean} [options.lazyLoad=true] - Enable lazy loading (default: true)
 * @param {string} [options.rootMargin='200px'] - Intersection observer root margin for preloading
 * @returns {Object} { isReady, isVisible, containerRef, containerId }
 */
export function usePixleeWidget(widgetId, options = {}) {
  const {
    containerId: customContainerId,
    lazyLoad = true,
    rootMargin = '200px', // Preload when within 200px of viewport
  } = options;

  const isScriptReady = usePixleeScript();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const initializedRef = useRef(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  // Generate unique container ID if not provided
  const containerId = customContainerId || `pixlee_container_${widgetId}`;

  // Use intersection observer for lazy loading
  const isVisible = useIntersectionObserver(containerRef, {
    rootMargin: lazyLoad ? rootMargin : '0px',
    threshold: 0,
    useOnce: true, // Only trigger once - widget stays loaded
  });

  // Determine if we should initialize
  // If lazy loading is disabled, initialize immediately when script is ready
  // If lazy loading is enabled, wait until element is visible
  const shouldInitialize = lazyLoad ? (isScriptReady && isVisible) : isScriptReady;

  useEffect(() => {
    // Wait for conditions to be met
    if (!shouldInitialize || !widgetId) {
      return;
    }

    const id = String(widgetId);

    // Same widget already initialized in this container - no action needed
    if (widgetIdRef.current === id && initializedRef.current) {
      return;
    }

    // Initialize widget with custom container ID
    if (initializeWidget(widgetId, containerId)) {
      widgetIdRef.current = id;
      initializedRef.current = true;
      setIsWidgetReady(true);
    }

    // Cleanup on unmount or widget change
    return () => {
      if (widgetIdRef.current === id) {
        cleanupWidget(containerId);
        widgetIdRef.current = null;
        initializedRef.current = false;
        setIsWidgetReady(false);
      }
    };
  }, [shouldInitialize, widgetId, containerId]);

  return {
    isReady: isWidgetReady,
    isVisible,
    containerRef,
    containerId,
  };
}
